import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Check, X, MessageCircle, Zap, Crown, Star, Layers, Sparkles, CreditCard, Loader2, Tag } from 'lucide-react';
import {
  PRICING_PLANS,
  PRICING_WHATSAPP_NUMBER,
  PRICING_BILLING_TABS,
  buildPricingWhatsAppMessage,
  resolvePlanPeriodLabel,
  resolvePlanPrice,
  resolvePlanTotalWithGstInr,
  formatInr,
  PRICING_GST_PERCENT,
  resolvePlanOriginalPrice,
  resolvePlanBillingCycle,
  formatPlanFeature,
  getPlanFeatureStruckText,
  isPlanFeatureStruckOnly,
  getVisiblePricingPlans,
} from '../config/pricingPlans';
import { openRazorpayPricingCheckout, consumePendingPricingCheckout } from '../utils/pricingRazorpay';
import {
  captureInfluencerCouponFromUrl,
  getStoredInfluencerCoupon,
  storeInfluencerCoupon,
  normalizeCouponCode,
} from '../utils/influencerAttribution';
import { validateInfluencerPromoCode } from '../api/influencer.api';
import { normalizeRunningPlanId } from '../constants/runningPlans';

/**
 * @param {{
 *   setActiveView?: (view: string) => void,
 *   showNotification?: (message: string, type?: string) => void,
 *   variant?: 'page' | 'modal',
 *   onClose?: () => void,
 *   runningPlan?: string,
 * }} props
 */
const PricingPage = ({ setActiveView, showNotification, variant = 'page', onClose, runningPlan = 'free' }) => {
  const isModal = variant === 'modal';
  const isFreeTrial = (normalizeRunningPlanId(runningPlan) || 'free') === 'free';
  const iconMap = {
    layers: Layers,
    sparkles: Sparkles,
    crown: Crown,
  };

  const [billingTab, setBillingTab] = useState('monthly');
  const [payingPlanId, setPayingPlanId] = useState(null);
  const [couponInput, setCouponInput] = useState(() => getStoredInfluencerCoupon() || '');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('role')?.toLowerCase() === 'admin'
  );

  useEffect(() => {
    setIsAdmin(localStorage.getItem('role')?.toLowerCase() === 'admin');
  }, []);

  const visiblePlans = getVisiblePricingPlans(isAdmin, billingTab);

  const notify = useCallback(
    (message, type = 'info') => {
      if (showNotification) showNotification(message, type);
      else if (type === 'error') console.error(message);
      else console.log(message);
    },
    [showNotification]
  );

  useEffect(() => {
    const fromUrl = captureInfluencerCouponFromUrl();
    if (fromUrl) setCouponInput(fromUrl);
  }, []);

  const applyCoupon = useCallback(async () => {
    const code = normalizeCouponCode(couponInput);
    if (!code) {
      setCouponApplied(null);
      return;
    }
    setCouponLoading(true);
    try {
      const res = await validateInfluencerPromoCode(code);
      const data = res.data?.data;
      storeInfluencerCoupon(code);
      setCouponApplied(data);
      notify(`Promo applied: ${data.discountPercent}% off`, 'success');
    } catch {
      setCouponApplied(null);
      notify('Invalid promo code', 'error');
    } finally {
      setCouponLoading(false);
    }
  }, [couponInput, notify]);

  useEffect(() => {
    const code = normalizeCouponCode(couponInput);
    if (code.length >= 4) {
      const t = setTimeout(() => applyCoupon(), 400);
      return () => clearTimeout(t);
    }
    setCouponApplied(null);
    return undefined;
  }, [couponInput, applyCoupon]);

  const discountedPrice = (basePrice) => {
    if (!couponApplied?.discountPercent) return basePrice;
    const discount = Math.round((basePrice * couponApplied.discountPercent) / 100);
    return Math.max(1, basePrice - discount);
  };

  const handlePurchase = useCallback(
    (plan) => {
      const token = localStorage.getItem('token');
      if (!token) {
        notify('Please log in to purchase a plan.', 'error');
        setActiveView?.('login');
        return;
      }

      const couponCode = couponApplied?.promoCode || normalizeCouponCode(couponInput) || undefined;

      setPayingPlanId(plan.id);
      openRazorpayPricingCheckout({
        plan,
        billingCycle: resolvePlanBillingCycle(plan),
        token,
        couponCode,
        onNotify: notify,
        onSuccess: () => {
          if (isModal) onClose?.();
        },
        onFinally: () => setPayingPlanId(null),
      });
    },
    [notify, setActiveView, couponApplied, couponInput, isModal, onClose]
  );

  const pendingResumeRef = useRef(false);
  useEffect(() => {
    if (isModal) return;
    if (pendingResumeRef.current) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const pending = consumePendingPricingCheckout();
    if (!pending?.planId) return;
    const plan = PRICING_PLANS.find((p) => p.id === pending.planId);
    if (!plan) return;
    const bc = resolvePlanBillingCycle(plan);
    pendingResumeRef.current = true;
    setPayingPlanId(plan.id);
    openRazorpayPricingCheckout({
      plan,
      billingCycle: bc,
      token,
      couponCode: couponApplied?.promoCode || getStoredInfluencerCoupon() || undefined,
      onNotify: notify,
      onFinally: () => setPayingPlanId(null),
    });
  }, [notify, couponApplied, isModal]);

  useEffect(() => {
    if (!isModal) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isModal, onClose]);

  const content = (
    <>
      <div className={`text-center ${isModal ? 'mb-6 sm:mb-8' : 'mb-12'}`}>
        {!isModal && (
          <div className="inline-flex items-center justify-center p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl mb-6">
            <Zap className="w-8 h-8 text-white" />
          </div>
        )}
        <h1 className={`font-bold text-gray-900 mb-2 sm:mb-4 ${isModal ? 'text-2xl sm:text-3xl' : 'text-4xl sm:text-5xl'}`}>
          {isModal
            ? isFreeTrial
              ? 'Upgrade your Free Trial'
              : 'Explore our plans'
            : 'Simple, Transparent Pricing'}
        </h1>
        <p className={`text-gray-600 max-w-2xl mx-auto ${isModal ? 'text-sm sm:text-base' : 'text-lg sm:text-xl'}`}>
          {isModal
            ? isFreeTrial
              ? 'Choose a plan to unlock more credits—or continue exploring on Free Trial.'
              : 'Review plan details anytime. You can keep your current plan and close this window.'
            : 'Choose the plan that fits your workflow—credit packs for AI concept generation and studio access.'}
        </p>
      </div>

      <div className={`flex justify-center ${isModal ? 'mb-6' : 'mb-10'}`}>
        <div className="inline-flex items-center rounded-2xl border border-gray-200 bg-white p-1 shadow-sm">
          {PRICING_BILLING_TABS.map((tab) => {
            const active = billingTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setBillingTab(tab.id)}
                className={`relative px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  active
                    ? 'bg-gradient-to-r from-[#5424e4] to-[#17bcf7] text-white shadow'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {tab.label}
                {tab.badge ? (
                  <span
                    className={`ml-2 text-[10px] font-bold uppercase tracking-wide ${
                      active ? 'text-white/90' : 'text-emerald-600'
                    }`}
                  >
                    {tab.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className={`max-w-md mx-auto ${isModal ? 'mb-6' : 'mb-10'}`}>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Tag size={16} className="text-violet-600" />
          Promo / influencer code
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            value={couponInput}
            onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
            placeholder="Enter code"
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2 font-mono text-sm focus:ring-2 focus:ring-violet-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={applyCoupon}
            disabled={couponLoading}
            className="px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 disabled:opacity-60"
          >
            {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
          </button>
        </div>
        {couponApplied && (
          <p className="text-sm text-emerald-600 mt-2">
            {couponApplied.discountPercent}% off applied
            {couponApplied.influencerName ? ` · ${couponApplied.influencerName}` : ''}
          </p>
        )}
      </div>

      <div
        className={`grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-5xl mx-auto ${
          isModal ? 'mb-6' : 'mb-12'
        } ${visiblePlans.length > 2 ? 'xl:grid-cols-3' : 'xl:grid-cols-2'}`}
      >
        {visiblePlans.map((plan) => {
          const Icon = iconMap[plan.icon] || Sparkles;
          const basePrice = resolvePlanPrice(plan);
          const price = discountedPrice(basePrice);
          const originalPrice = resolvePlanOriginalPrice(plan);
          const periodLabel = resolvePlanPeriodLabel(plan);
          const totalWithGst = resolvePlanTotalWithGstInr(price);
          const isPayingThis = payingPlanId === plan.id;
          const checkoutBusy = payingPlanId !== null;
          const hasDiscount = couponApplied && price < basePrice;
          const showOriginalPrice = originalPrice && !hasDiscount;
          return (
            <div
              key={plan.id}
              className={`relative bg-white rounded-3xl shadow-xl border-2 overflow-hidden transition-all duration-300 hover:shadow-2xl hover:scale-[1.02] ${
                plan.popular ? 'border-purple-400' : 'border-gray-200'
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-bl-2xl font-bold text-sm flex items-center gap-1">
                  <Star size={16} fill="white" />
                  MOST POPULAR
                </div>
              )}

              {plan.image && (
                <div className="w-full aspect-[4/3] overflow-hidden bg-gray-50">
                  <img
                    src={plan.image}
                    alt={plan.name}
                    loading="lazy"
                    className="w-full h-full object-cover object-center"
                  />
                </div>
              )}

              <div className={`bg-gradient-to-br ${plan.bgGradient} p-6 sm:p-8 pb-6`}>
                <div className={`inline-flex p-3 bg-gradient-to-r ${plan.gradient} rounded-2xl mb-4`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                <div className="mb-2 space-y-1">
                  {hasDiscount && (
                    <div className="text-base sm:text-lg text-gray-400 line-through">
                      ₹{basePrice.toLocaleString('en-IN')}
                    </div>
                  )}
                  {showOriginalPrice && (
                    <div className="text-base sm:text-lg text-gray-400 line-through">
                      ₹{originalPrice.toLocaleString('en-IN')}
                    </div>
                  )}
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-3xl sm:text-4xl font-bold text-gray-900 leading-none">
                      ₹{price.toLocaleString('en-IN')}
                    </span>
                    <span className="text-sm sm:text-base text-gray-600 font-semibold">
                      + {PRICING_GST_PERCENT}% GST
                    </span>
                    <span className="text-sm sm:text-base text-gray-600 font-medium">{periodLabel}</span>
                  </div>
                  <div className="text-sm text-gray-500">
                    Total ₹{formatInr(totalWithGst)} incl. GST
                  </div>
                </div>
              </div>

              <div className="p-6 sm:p-8">
                <ul className="space-y-3 mb-4">
                  {plan.features.map((feature, index) => {
                    const struckText = getPlanFeatureStruckText(feature);
                    const mainText = formatPlanFeature(feature);
                    if (isPlanFeatureStruckOnly(feature)) {
                      return (
                        <li key={index} className="pl-9 text-sm text-gray-400 line-through">
                          {mainText}
                        </li>
                      );
                    }
                    return (
                      <li key={index} className="flex items-start gap-3">
                        <div
                          className={`flex-shrink-0 w-6 h-6 rounded-full bg-gradient-to-r ${plan.gradient} flex items-center justify-center mt-0.5`}
                        >
                          <Check size={14} className="text-white font-bold" />
                        </div>
                        <span className="text-gray-700 font-medium">
                          {struckText ? (
                            <>
                              <span className="line-through text-gray-400 mr-1.5">{struckText}</span>
                              {mainText}
                            </>
                          ) : (
                            mainText
                          )}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {plan.exclusions && plan.exclusions.length > 0 && (
                  <ul className="space-y-3 mb-6">
                    {plan.exclusions.map((exc, index) => (
                      <li key={`exc-${index}`} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center mt-0.5">
                          <X size={14} className="text-gray-500" />
                        </div>
                        <span className="text-gray-500 text-sm">{exc}</span>
                      </li>
                    ))}
                  </ul>
                )}

                <button
                  type="button"
                  disabled={checkoutBusy}
                  onClick={() => handlePurchase(plan)}
                  className={`w-full bg-gradient-to-r ${plan.gradient} text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-[1.02] flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:scale-100`}
                >
                  {isPayingThis ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Processing…
                    </>
                  ) : (
                    <>
                      <CreditCard size={20} />
                      Buy now
                    </>
                  )}
                </button>

                <a
                  href={`https://wa.me/${PRICING_WHATSAPP_NUMBER}?text=${encodeURIComponent(
                    buildPricingWhatsAppMessage(plan)
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full border-2 border-green-500 text-green-600 py-3.5 rounded-xl font-bold text-lg hover:bg-green-50 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <MessageCircle size={20} />
                  Chat Now
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {isModal ? (
        <div className="text-center pt-2 pb-1">
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-sm sm:text-base font-semibold text-[#5424e4] hover:text-[#3d18b8] underline underline-offset-4 decoration-2"
          >
            {isFreeTrial ? 'Continue with Free Trial' : 'Maybe later'}
          </button>
          <p className="text-xs text-gray-500 mt-2">You can open Pricing anytime from the sidebar.</p>
        </div>
      ) : (
        <div className="text-center">
          <p className="text-gray-600 mb-4">Questions about pricing or enterprise needs?</p>
          <a
            href={`https://wa.me/${PRICING_WHATSAPP_NUMBER}?text=${encodeURIComponent(
              buildPricingWhatsAppMessage(visiblePlans[0] || PRICING_PLANS[0])
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 text-white font-semibold rounded-xl hover:bg-green-600 transition"
          >
            <MessageCircle size={20} />
            Chat on WhatsApp
          </a>
        </div>
      )}
    </>
  );

  if (isModal) {
    return (
      <div
        className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-promo-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div className="relative w-full sm:max-w-5xl max-h-[92dvh] sm:max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl bg-gradient-to-br from-blue-50 via-white to-purple-50 shadow-2xl">
          <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-200/80 bg-white/90 backdrop-blur px-4 sm:px-6 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <div className="inline-flex items-center justify-center p-1.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl shrink-0">
                <Zap className="w-4 h-4 text-white" />
              </div>
              <h2 id="pricing-promo-title" className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                Plans
              </h2>
            </div>
            <button
              type="button"
              onClick={() => onClose?.()}
              className="p-2 rounded-lg text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>
          <div className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8">{content}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-8 sm:pb-12">{content}</div>
    </div>
  );
};

export default PricingPage;
